"use client";

import React, { useState } from "react";
import { StyledSection } from "./platformHowTo.styles";
import { trackEvent } from '@/lib/track';

export interface IPlatformGuide {
  platform: string;
  // Optional. Omitted by hubs whose widget has no published platform spoke,
  // where the panel CTA would otherwise point at a 404. The steps still render.
  href?: string;
  steps: string[];
}

interface IPlatformHowToProps {
  title?: string;
  eyebrow?: string;
  widgetName?: string;
  guides: IPlatformGuide[];
  tabEventName?: string;
  ctaEventName?: string;
}

export const PlatformHowTo = ({
  title = "How to add Instagram Feed to your website",
  eyebrow = "Platform-specific guides",
  widgetName = "Instagram Feed",
  guides,
  tabEventName,
  ctaEventName,
}: IPlatformHowToProps) => {
  const [openIndex, setOpenIndex] = useState<number>(0);
  if (!guides.length) return null;

  return (
    <StyledSection>
      <div className="inner">
        <header>
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          <p className="lead">
            Pick your platform. Each takes under 4 minutes.
          </p>
        </header>

        <div className="tabs" role="tablist">
          {guides.map((g, i) => (
            <button
              key={g.platform}
              className={`tab ${i === openIndex ? "active" : ""}`}
              onClick={() => {
                setOpenIndex(i);
                if (tabEventName)
                  trackEvent(tabEventName, { platform: g.platform });
              }}
              role="tab"
              aria-selected={i === openIndex}
              aria-controls={`platform-panel-${i}`}
            >
              {g.platform}
            </button>
          ))}
        </div>

        <div className="panels">
          {guides.map((g, i) => (
            <div
              key={g.platform}
              id={`platform-panel-${i}`}
              role="tabpanel"
              hidden={i !== openIndex}
              className="panel"
            >
              <ol>
                {g.steps.map((s, idx) => (
                  <li key={idx}>
                    <span className="step-num">{idx + 1}</span>
                    <span className="step-text">{s}</span>
                  </li>
                ))}
              </ol>
              {g.href && (
                <a
                  className="panel-cta"
                  href={g.href}
                  onClick={() => {
                    if (ctaEventName)
                      trackEvent(ctaEventName, { platform: g.platform });
                  }}
                >
                  Add {widgetName} to {g.platform}
                  <span aria-hidden="true">→</span>
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </StyledSection>
  );
};
